import type { ReactNode } from 'react';
import { AppFilePicker } from './AppFilePicker';
import { Icon, type IconName } from './Icon';

/**
 * Раздел карточки — блок с заголовком над ним ([D-36](docs/DECISIONS.md)).
 *
 * Из блоков собираются пять экранов-карточек: дело, клиент, задача, событие, настройки.
 * Списки — дела, задачи, клиенты, календарь, документы, поиск — блоками **не**
 * оформляются: они однородны, и блоки разрезали бы прокрутку на куски.
 */
export function AppSectionCard({
  title,
  action,
  children,
}: {
  title?: string;
  action?: { label: string; onClick: () => void };
  children: ReactNode;
}) {
  return (
    <section className="section">
      {title || action ? (
        <div className="section__header">
          <span>{title}</span>
          {action ? (
            <button type="button" className="section__header-action" onClick={action.onClick}>
              {action.label}
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="section__body">{children}</div>
    </section>
  );
}

/**
 * Строка внутри блока: одна иконка 24 и единый левый край.
 *
 * `action` набирает строку цветом действия и с иконкой — так приглашение «Добавить
 * описание» отличается от самого описания. До D-36 и то и другое рисовалось одним
 * серым текстом, и заполненное дело выглядело пустым.
 */
export function AppSectionRow({
  icon,
  title,
  subtitle,
  onClick,
  action = false,
  trailing,
  picker,
}: {
  icon: IconName;
  title: string;
  subtitle?: string;
  onClick?: () => void;
  action?: boolean;
  trailing?: ReactNode;
  /**
   * Строка, которая открывает системное окно выбора файла, — тем же способом,
   * что и пункт листа ([D-32](docs/DECISIONS.md)): поле `<input type="file">`
   * живёт в одном месте, а строка остаётся строкой блока.
   */
  picker?: { accept?: string; multiple?: boolean; onFiles: (files: File[]) => void };
}) {
  const className = `section-row ${action ? 'section-row--action' : ''}`;
  const content = (
    <>
      <Icon name={icon} size={24} className="section-row__icon" />
      <span className="section-row__texts">
        <span className="section-row__title">{title}</span>
        {subtitle ? <span className="section-row__subtitle">{subtitle}</span> : null}
      </span>
      {trailing}
    </>
  );

  if (picker) {
    return (
      <AppFilePicker
        label={title}
        accept={picker.accept}
        multiple={picker.multiple}
        variant="row"
        onFiles={picker.onFiles}
      >
        {content}
      </AppFilePicker>
    );
  }

  if (!onClick) return <div className={className}>{content}</div>;

  /*
   * Нажимаемая строка с кнопкой справа — это **две** кнопки рядом, а не одна в другой.
   *
   * Вложенная кнопка в HTML недопустима, и браузер не «чинит» её сам, когда разметку
   * строит React: нажатие на внутреннюю доходило и до внешней, срабатывали оба
   * обработчика — и побеждал переход. На живом iPhone 09.09.2026 это выглядело так:
   * карандаш «Сменить клиента» в карточке дела не менял клиента, а открывал его
   * карточку. Оттуда единственным способом «убрать» клиента было «Удалить» — и юрист
   * вычёркивал клиента из записной книжки, желая всего лишь отвязать его от дела.
   */
  if (trailing) {
    return (
      <div className={className}>
        <button type="button" className="section-row__hit" onClick={onClick}>
          <Icon name={icon} size={24} className="section-row__icon" />
          <span className="section-row__texts">
            <span className="section-row__title">{title}</span>
            {subtitle ? <span className="section-row__subtitle">{subtitle}</span> : null}
          </span>
        </button>
        {trailing}
      </div>
    );
  }

  return (
    <button type="button" className={className} onClick={onClick}>
      {content}
    </button>
  );
}

/** Разделитель внутри блока: начинается от текста, а не от края блока. */
export function AppSectionDivider() {
  return <hr className="section-divider" />;
}
